import React, { useMemo } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Badge,
} from 'reactstrap';

const safeParseDate = (value) => {
  if (!value) return null;
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'string') return new Date(value);
  if (value?.toMillis) return new Date(value.toMillis());
  if (value?.seconds) return new Date(value.seconds * 1000);
  return null;
};

const getHeatColor = (count, max) => {
  if (count === 0) return '#ebedf0';
  const ratio = count / max;
  if (ratio <= 0.25) return '#9be9a8';
  if (ratio <= 0.5) return '#40c463';
  if (ratio <= 0.75) return '#30a14e';
  return '#216e39';
};

const ActivityStreaksCard = ({ testSummaries = [] }) => {
  const dailyActivity = useMemo(() => {
    const dayMap = {};
    testSummaries.forEach(test => {
      const date = safeParseDate(test.completedAt);
      if (!date || isNaN(date.getTime())) return;
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      dayMap[key] = (dayMap[key] || 0) + (test.totalQuestions || 0);
    });

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayKey = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`;

    let streak = 0;
    let checkDate = new Date(today);
    if (!dayMap[todayKey] && dayMap[yesterdayKey]) {
      checkDate = new Date(yesterday);
    }
    while (true) {
      const key = `${checkDate.getFullYear()}-${String(checkDate.getMonth() + 1).padStart(2, '0')}-${String(checkDate.getDate()).padStart(2, '0')}`;
      if (dayMap[key]) {
        streak++;
        checkDate.setDate(checkDate.getDate() - 1);
      } else {
        break;
      }
    }

    const activeDates = Object.keys(dayMap).sort();
    let longestStreak = 0;
    let currentRun = 0;
    for (let i = 0; i < activeDates.length; i++) {
      if (i === 0) {
        currentRun = 1;
      } else {
        const prev = new Date(activeDates[i - 1] + 'T00:00:00');
        const curr = new Date(activeDates[i] + 'T00:00:00');
        const diffDays = Math.round((curr - prev) / (1000 * 60 * 60 * 24));
        currentRun = diffDays === 1 ? currentRun + 1 : 1;
      }
      longestStreak = Math.max(longestStreak, currentRun);
    }

    const heatMapDays = [];
    for (let i = 89; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      heatMapDays.push({
        date: new Date(d),
        dateKey: key,
        count: dayMap[key] || 0,
        dayOfWeek: d.getDay()
      });
    }

    const maxCount = Math.max(...heatMapDays.map(d => d.count), 1);

    const startOfWeek = new Date(today);
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
    let weekTotal = 0;
    for (let i = 0; i < 7; i++) {
      const d = new Date(startOfWeek);
      d.setDate(d.getDate() + i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      weekTotal += dayMap[key] || 0;
    }

    const todayTotal = dayMap[todayKey] || 0;

    return { streak, longestStreak, heatMapDays, maxCount, weekTotal, todayTotal };
  }, [testSummaries]);

  // Build weeks grid for heatmap
  const weeks = useMemo(() => {
    const w = [];
    let currentWeek = new Array(7).fill(null);
    dailyActivity.heatMapDays.forEach((day, i) => {
      currentWeek[day.dayOfWeek] = day;
      if (day.dayOfWeek === 6 || i === dailyActivity.heatMapDays.length - 1) {
        w.push(currentWeek);
        currentWeek = new Array(7).fill(null);
      }
    });
    return w;
  }, [dailyActivity.heatMapDays]);

  const monthLabels = useMemo(() => {
    const labels = [];
    let lastMonth = -1;
    weeks.forEach((week, weekIdx) => {
      const firstDay = week.find(d => d !== null);
      if (firstDay) {
        const month = firstDay.date.getMonth();
        if (month !== lastMonth) {
          labels.push({ weekIdx, label: firstDay.date.toLocaleDateString('en-US', { month: 'short' }) });
          lastMonth = month;
        }
      }
    });
    return labels;
  }, [weeks]);

  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const labelWidth = 28;
  const gap = 3;
  const numWeeks = weeks.length;

  return (
    <Row className="mb-4">
      <Col lg="8" md="7" className="mb-3 mb-lg-0">
        <Card className="shadow-sm h-100">
          <CardHeader className="border-0">
            <h4 className="mb-0">
              <i className="fas fa-th text-success mr-2"></i>
              Activity (last 90 days)
              {dailyActivity.todayTotal > 0 && (
                <Badge color="success" className="ml-2" style={{ fontSize: '0.7rem', verticalAlign: 'middle' }}>
                  {dailyActivity.todayTotal} today
                </Badge>
              )}
            </h4>
          </CardHeader>
          <CardBody className="pt-0">
            <div style={{ width: '100%' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: `${labelWidth}px repeat(${numWeeks}, 1fr)`,
                gap: `${gap}px`,
                marginBottom: '4px'
              }}>
                <div />
                {weeks.map((_, weekIdx) => {
                  const monthLabel = monthLabels.find(m => m.weekIdx === weekIdx);
                  return (
                    <div key={weekIdx} style={{ fontSize: '10px', color: '#8898aa', textAlign: 'left' }}>
                      {monthLabel ? monthLabel.label : ''}
                    </div>
                  );
                })}
              </div>
              {dayLabels.map((label, dayIdx) => (
                <div
                  key={dayIdx}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: `${labelWidth}px repeat(${numWeeks}, 1fr)`,
                    gap: `${gap}px`,
                    marginBottom: `${gap}px`
                  }}
                >
                  <div style={{
                    fontSize: '10px',
                    color: '#8898aa',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    paddingRight: '4px'
                  }}>
                    {dayIdx % 2 === 1 ? label.charAt(0) : ''}
                  </div>
                  {weeks.map((week, weekIdx) => {
                    const day = week[dayIdx];
                    return (
                      <div
                        key={weekIdx}
                        title={day ? `${day.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}: ${day.count} question${day.count !== 1 ? 's' : ''}` : ''}
                        style={{
                          aspectRatio: '1',
                          backgroundColor: day ? getHeatColor(day.count, dailyActivity.maxCount) : 'transparent',
                          borderRadius: '3px',
                          width: '100%'
                        }}
                      />
                    );
                  })}
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: '8px', gap: '3px' }}>
                <span style={{ fontSize: '10px', color: '#8898aa', marginRight: '4px' }}>Less</span>
                {['#ebedf0', '#9be9a8', '#40c463', '#30a14e', '#216e39'].map((color, i) => (
                  <div
                    key={i}
                    style={{
                      width: 14,
                      height: 14,
                      backgroundColor: color,
                      borderRadius: '2px'
                    }}
                  />
                ))}
                <span style={{ fontSize: '10px', color: '#8898aa', marginLeft: '4px' }}>More</span>
              </div>
            </div>
          </CardBody>
        </Card>
      </Col>
      <Col lg="4" md="5">
        <Card className="shadow-sm mb-3" style={{ background: 'linear-gradient(87deg, #fb6340 0, #fbb140 100%)' }}>
          <CardBody className="py-3 text-white">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <p className="text-white mb-1 small text-uppercase">Current Streak</p>
                <h2 className="mb-0 font-weight-bold text-white">{dailyActivity.streak} day{dailyActivity.streak !== 1 ? 's' : ''}</h2>
              </div>
              <div className="icon icon-shape bg-white rounded-circle shadow" style={{ color: '#fb6340' }}>
                <i className="fas fa-fire"></i>
              </div>
            </div>
          </CardBody>
        </Card>
        <Card className="shadow-sm mb-3" style={{ background: 'linear-gradient(87deg, #8965e0 0, #bc65e0 100%)' }}>
          <CardBody className="py-3 text-white">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <p className="text-white mb-1 small text-uppercase">Longest Streak</p>
                <h2 className="mb-0 font-weight-bold text-white">{dailyActivity.longestStreak} day{dailyActivity.longestStreak !== 1 ? 's' : ''}</h2>
              </div>
              <div className="icon icon-shape bg-white rounded-circle shadow" style={{ color: '#8965e0' }}>
                <i className="fas fa-trophy"></i>
              </div>
            </div>
          </CardBody>
        </Card>
        <Card className="shadow-sm" style={{ background: 'linear-gradient(87deg, #11cdef 0, #1171ef 100%)' }}>
          <CardBody className="py-3 text-white">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <p className="text-white mb-1 small text-uppercase">This Week</p>
                <h2 className="mb-0 font-weight-bold text-white">{dailyActivity.weekTotal} question{dailyActivity.weekTotal !== 1 ? 's' : ''}</h2>
              </div>
              <div className="icon icon-shape bg-white rounded-circle shadow" style={{ color: '#11cdef' }}>
                <i className="fas fa-calendar-week"></i>
              </div>
            </div>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default ActivityStreaksCard;
